import { z } from 'zod';

/**
 * Supported event types for tracking
 */
export enum EventType {
  // Action events
  ACTION_VIEWED = 'ACTION_VIEWED',
  ACTION_RENDERED = 'ACTION_RENDERED',
  ACTION_INTERACTED = 'ACTION_INTERACTED',
  ACTION_REJECTED_REASON_PROVIDED = 'ACTION_REJECTED_REASON_PROVIDED',
  ACTION_EDITED = 'ACTION_EDITED',

  STRIPE_CONNECT_ONBOARDING_STARTED = 'STRIPE_CONNECT_ONBOARDING_STARTED',

  ENCOUNTER_SUBMITTED = 'ENCOUNTER_SUBMITTED',

  // Generic events
  FEEDBACK_SUBMITTED = 'FEEDBACK_SUBMITTED',
  EXPRESSED_INTEREST = 'EXPRESSED_INTEREST',
  EXPRESSED_NOT_INTERESTED = 'EXPRESSED_NOT_INTERESTED',

  // Document events
  PDF_INTERACTED = 'PDF_INTERACTED',

  // Toolbar events
  TOOLBAR_CLICKED = 'TOOLBAR_CLICKED',

  // JotCoach events
  JOTCOACH_OVERVIEW_RENDERED = 'JOTCOACH_OVERVIEW_RENDERED',
  JOTCOACH_OVERVIEW_VIEWED = 'JOTCOACH_OVERVIEW_VIEWED',
  JOTCOACH_OVERVIEW_EXPANDED = 'JOTCOACH_OVERVIEW_EXPANDED',
  JOTCOACH_OVERVIEW_COLLAPSED = 'JOTCOACH_OVERVIEW_COLLAPSED',
  JOTCOACH_GAP_RENDERED = 'JOTCOACH_GAP_RENDERED',
  JOTCOACH_GAP_VIEWED = 'JOTCOACH_GAP_VIEWED',
  JOTCOACH_GAP_EXPANDED = 'JOTCOACH_GAP_EXPANDED',
  JOTCOACH_GAP_COLLAPSED = 'JOTCOACH_GAP_COLLAPSED',
  JOTCOACH_GAP_COPIED = 'JOTCOACH_GAP_COPIED',
  JOTCOACH_QUESTION_RENDERED = 'JOTCOACH_QUESTION_RENDERED',
  JOTCOACH_QUESTION_VIEWED = 'JOTCOACH_QUESTION_VIEWED',
  JOTCOACH_FEEDBACK_USEFUL = 'JOTCOACH_FEEDBACK_USEFUL',
  JOTCOACH_FEEDBACK_NOT_USEFUL = 'JOTCOACH_FEEDBACK_NOT_USEFUL',
  JOTCOACH_FEEDBACK_COMMENT = 'JOTCOACH_FEEDBACK_COMMENT',
  // Add more event types as needed...
}

/**
 * Interaction types for ACTION_INTERACTED events
 */
export enum InteractionType {
  ACCEPT = 'ACCEPT',
  REJECT = 'REJECT',
  UPDATE = 'UPDATE',
  EXPAND = 'EXPAND',
  COLLAPSE = 'COLLAPSE',
}

// Base schema for all action-related events
export const BaseActionSchema = z.object({
  action_id: z.string(),
  action_type: z.string(),
  encounter_id: z.string().optional().nullable(),
  patient_id: z.string().optional().nullable(),
});

// Zod schema for ACTION_VIEWED event data - extends the base schema
export const ActionViewedSchema = BaseActionSchema;

// Zod schema for ACTION_RENDERED event data - extends the base schema
export const ActionRenderedSchema = BaseActionSchema;

// Zod schema for ACTION_INTERACTED event data - extends the base schema and adds interaction type
export const ActionInteractedSchema = BaseActionSchema.extend({
  interaction_type: z.nativeEnum(InteractionType),
});

// Zod schema for ACTION_REJECTED_REASON_PROVIDED event data - extends the base schema and adds rejection reason
export const ActionRejectedSchema = BaseActionSchema.extend({
  rejection_reason: z.string(), // e.g., "wrong_suggestion" | "dont_care_for_feature" | "other"
  feedback: z.string().optional().nullable(), // additional comment if provided
});

// Zod schema for ACTION_EDITED event data - tracks when users edit actions
export const ActionEditedSchema = BaseActionSchema.extend({
  context: z.string(), // e.g., "rejection_modal", "card"
  original_action_payload: z.any(),
  updated_action_payload: z.any(),
});

// Zod schema for ENCOUNTER_SUBMITTED event data
export const CreateNoteFromEncounterButtonClickedSchema = z.object({
  encounter_id: z.string(),
  click_context: z.string(),
});

// Zod schema for FEEDBACK_SUBMITTED event data - Generic feedback event
export const FeedbackSubmittedSchema = z.object({
  feedback_type: z.enum(['thumbs_up', 'thumbs_down']),
  context: z.record(z.any()), // Flexible metadata about what the feedback is for
  comment: z.string().optional().nullable(),
  session_id: z.string().optional().nullable(),
});

// Zod schema for STRIPE_CONNECT_ONBOARDING_STARTED event data - empty object as requested
export const StripeConnectOnboardingStartedSchema = z.object({});

// Zod schema for EXPRESSED_INTEREST event data - tracks fake door test clicks
export const ExpressedInterestSchema = z.object({
  feature: z.string(), // e.g., 'communications', 'reports_analytics', 'bill_insurance'
});

// Zod schema for EXPRESSED_NOT_INTERESTED event data - tracks when users dismiss or decline features
export const ExpressedNotInterestedSchema = z.object({
  feature: z.string(), // e.g., 'eligibility-checks', 'jotcoach', etc.
});

// Zod schema for PDF_INTERACTED event data
export const PDFInteractedSchema = z.object({
  interaction_type: z.enum(['download', 'view', 'print', 'save_to_portal']),

  // User info
  user_type: z.enum(['provider', 'patient', 'staff', 'anonymous']),
  patient_id: z.string().optional().nullable(),

  // Access details
  access_method: z.enum([
    'preview_modal',
    'direct_link',
    'patient_portal',
    'provider_portal',
    'email_link',
  ]),
  user_agent: z.string().optional().nullable(),

  // Document info
  document_type: z.string().optional().nullable(), // "lab_order", "note", etc.
  document_id: z.string().optional().nullable(),
});

// Zod schema for TOOLBAR_CLICKED event data
export const ToolbarClickedSchema = z.object({
  feature: z.string(), // e.g., 'lab_order', 'payment_request', etc.
  user_agent: z.string().optional().nullable(),
  metadata: z.record(z.any()).optional(), // Feature-specific data (e.g., patient_id for lab_order)
});

// Base schema for JotCoach events
export const BaseJotCoachSchema = z.object({
  jotcoach_session_id: z.string(),
  note_id: z.string(),
  item_type: z.enum(['overview', 'gap', 'question']),
  item_id: z.string(),
});

// Zod schema for JOTCOACH_OVERVIEW_RENDERED event data
export const JotCoachOverviewRenderedSchema = BaseJotCoachSchema.extend({
  quality_score: z.number().optional().nullable(),
});

// Zod schema for JOTCOACH_OVERVIEW_VIEWED event data
export const JotCoachOverviewViewedSchema = BaseJotCoachSchema.extend({
  quality_score: z.number().optional().nullable(),
});

// Zod schema for JOTCOACH_OVERVIEW_EXPANDED event data
export const JotCoachOverviewExpandedSchema = BaseJotCoachSchema.extend({
  quality_score: z.number().optional().nullable(),
});

// Zod schema for JOTCOACH_OVERVIEW_COLLAPSED event data
export const JotCoachOverviewCollapsedSchema = BaseJotCoachSchema.extend({
  quality_score: z.number().optional().nullable(),
});

// Zod schema for JOTCOACH_GAP_RENDERED event data
export const JotCoachGapRenderedSchema = BaseJotCoachSchema.extend({
  section_id: z.string(),
  priority: z.enum(['High', 'Medium', 'Low']).optional().nullable(),
  gap_title: z.string().optional().nullable(),
});

// Zod schema for JOTCOACH_GAP_VIEWED event data
export const JotCoachGapViewedSchema = BaseJotCoachSchema.extend({
  section_id: z.string(),
  priority: z.enum(['High', 'Medium', 'Low']).optional().nullable(),
  gap_title: z.string().optional().nullable(),
});

// Zod schema for JOTCOACH_GAP_EXPANDED event data
export const JotCoachGapExpandedSchema = BaseJotCoachSchema.extend({
  section_id: z.string(),
  priority: z.enum(['High', 'Medium', 'Low']).optional().nullable(),
  gap_title: z.string().optional().nullable(),
});

// Zod schema for JOTCOACH_GAP_COLLAPSED event data
export const JotCoachGapCollapsedSchema = BaseJotCoachSchema.extend({
  section_id: z.string(),
  priority: z.enum(['High', 'Medium', 'Low']).optional().nullable(),
  gap_title: z.string().optional().nullable(),
});

// Zod schema for JOTCOACH_GAP_COPIED event data
export const JotCoachGapCopiedSchema = BaseJotCoachSchema.extend({
  section_id: z.string(),
  priority: z.enum(['High', 'Medium', 'Low']).optional().nullable(),
  gap_title: z.string().optional().nullable(),
});

// Zod schema for JOTCOACH_QUESTION_RENDERED event data
export const JotCoachQuestionRenderedSchema = BaseJotCoachSchema.extend({
  section_id: z.string(),
  question_title: z.string().optional().nullable(),
});

// Zod schema for JOTCOACH_QUESTION_VIEWED event data
export const JotCoachQuestionViewedSchema = BaseJotCoachSchema.extend({
  section_id: z.string(),
  question_title: z.string().optional().nullable(),
});

// Zod schema for JOTCOACH_FEEDBACK_USEFUL event data
export const JotCoachFeedbackUsefulSchema = BaseJotCoachSchema.extend({
  section_id: z.string().optional().nullable(),
});

// Zod schema for JOTCOACH_FEEDBACK_NOT_USEFUL event data
export const JotCoachFeedbackNotUsefulSchema = BaseJotCoachSchema.extend({
  section_id: z.string().optional().nullable(),
});

// Zod schema for JOTCOACH_FEEDBACK_COMMENT event data
export const JotCoachFeedbackCommentSchema = BaseJotCoachSchema.extend({
  section_id: z.string().optional().nullable(),
});

// Map event types to their schemas
export const EVENT_SCHEMAS = {
  [EventType.ACTION_VIEWED]: ActionViewedSchema,
  [EventType.ACTION_RENDERED]: ActionRenderedSchema,
  [EventType.ACTION_INTERACTED]: ActionInteractedSchema,
  [EventType.ACTION_REJECTED_REASON_PROVIDED]: ActionRejectedSchema,
  [EventType.ACTION_EDITED]: ActionEditedSchema,
  [EventType.STRIPE_CONNECT_ONBOARDING_STARTED]: StripeConnectOnboardingStartedSchema,
  [EventType.ENCOUNTER_SUBMITTED]: CreateNoteFromEncounterButtonClickedSchema,
  [EventType.FEEDBACK_SUBMITTED]: FeedbackSubmittedSchema,
  [EventType.EXPRESSED_INTEREST]: ExpressedInterestSchema,
  [EventType.EXPRESSED_NOT_INTERESTED]: ExpressedNotInterestedSchema,
  [EventType.PDF_INTERACTED]: PDFInteractedSchema,
  [EventType.TOOLBAR_CLICKED]: ToolbarClickedSchema,
  [EventType.JOTCOACH_OVERVIEW_RENDERED]: JotCoachOverviewRenderedSchema,
  [EventType.JOTCOACH_OVERVIEW_VIEWED]: JotCoachOverviewViewedSchema,
  [EventType.JOTCOACH_OVERVIEW_EXPANDED]: JotCoachOverviewExpandedSchema,
  [EventType.JOTCOACH_OVERVIEW_COLLAPSED]: JotCoachOverviewCollapsedSchema,
  [EventType.JOTCOACH_GAP_RENDERED]: JotCoachGapRenderedSchema,
  [EventType.JOTCOACH_GAP_VIEWED]: JotCoachGapViewedSchema,
  [EventType.JOTCOACH_GAP_EXPANDED]: JotCoachGapExpandedSchema,
  [EventType.JOTCOACH_GAP_COLLAPSED]: JotCoachGapCollapsedSchema,
  [EventType.JOTCOACH_GAP_COPIED]: JotCoachGapCopiedSchema,
  [EventType.JOTCOACH_QUESTION_RENDERED]: JotCoachQuestionRenderedSchema,
  [EventType.JOTCOACH_QUESTION_VIEWED]: JotCoachQuestionViewedSchema,
  [EventType.JOTCOACH_FEEDBACK_USEFUL]: JotCoachFeedbackUsefulSchema,
  [EventType.JOTCOACH_FEEDBACK_NOT_USEFUL]: JotCoachFeedbackNotUsefulSchema,
  [EventType.JOTCOACH_FEEDBACK_COMMENT]: JotCoachFeedbackCommentSchema,
} as const;

// Infer TypeScript types from Zod schemas
export type ActionViewedEvent = z.infer<typeof ActionViewedSchema>;
export type ActionRenderedEvent = z.infer<typeof ActionRenderedSchema>;
export type ActionInteractedEvent = z.infer<typeof ActionInteractedSchema>;
export type ActionRejectedEvent = z.infer<typeof ActionRejectedSchema>;
export type ActionEditedEvent = z.infer<typeof ActionEditedSchema>;
export type StripeConnectOnboardingStartedEvent = z.infer<
  typeof StripeConnectOnboardingStartedSchema
>;
export type CreateNoteFromEncounterButtonClickedEvent = z.infer<
  typeof CreateNoteFromEncounterButtonClickedSchema
>;
export type FeedbackSubmittedEvent = z.infer<typeof FeedbackSubmittedSchema>;
export type ExpressedInterestEvent = z.infer<typeof ExpressedInterestSchema>;
export type ExpressedNotInterestedEvent = z.infer<typeof ExpressedNotInterestedSchema>;
export type PDFInteractedEvent = z.infer<typeof PDFInteractedSchema>;
export type ToolbarClickedEvent = z.infer<typeof ToolbarClickedSchema>;
export type JotCoachOverviewRenderedEvent = z.infer<typeof JotCoachOverviewRenderedSchema>;
export type JotCoachOverviewViewedEvent = z.infer<typeof JotCoachOverviewViewedSchema>;
export type JotCoachOverviewExpandedEvent = z.infer<typeof JotCoachOverviewExpandedSchema>;
export type JotCoachOverviewCollapsedEvent = z.infer<typeof JotCoachOverviewCollapsedSchema>;
export type JotCoachGapRenderedEvent = z.infer<typeof JotCoachGapRenderedSchema>;
export type JotCoachGapViewedEvent = z.infer<typeof JotCoachGapViewedSchema>;
export type JotCoachGapExpandedEvent = z.infer<typeof JotCoachGapExpandedSchema>;
export type JotCoachGapCollapsedEvent = z.infer<typeof JotCoachGapCollapsedSchema>;
export type JotCoachGapCopiedEvent = z.infer<typeof JotCoachGapCopiedSchema>;
export type JotCoachQuestionRenderedEvent = z.infer<typeof JotCoachQuestionRenderedSchema>;
export type JotCoachQuestionViewedEvent = z.infer<typeof JotCoachQuestionViewedSchema>;
export type JotCoachFeedbackUsefulEvent = z.infer<typeof JotCoachFeedbackUsefulSchema>;
export type JotCoachFeedbackNotUsefulEvent = z.infer<typeof JotCoachFeedbackNotUsefulSchema>;
export type JotCoachFeedbackCommentEvent = z.infer<typeof JotCoachFeedbackCommentSchema>;

// Type for any event data
export type EventData =
  | ActionViewedEvent
  | ActionRenderedEvent
  | ActionInteractedEvent
  | ActionRejectedEvent
  | ActionEditedEvent
  | StripeConnectOnboardingStartedEvent
  | CreateNoteFromEncounterButtonClickedEvent
  | FeedbackSubmittedEvent
  | ExpressedInterestEvent
  | ExpressedNotInterestedEvent
  | PDFInteractedEvent
  | ToolbarClickedEvent
  | JotCoachOverviewRenderedEvent
  | JotCoachOverviewViewedEvent
  | JotCoachOverviewExpandedEvent
  | JotCoachOverviewCollapsedEvent
  | JotCoachGapRenderedEvent
  | JotCoachGapViewedEvent
  | JotCoachGapExpandedEvent
  | JotCoachGapCollapsedEvent
  | JotCoachGapCopiedEvent
  | JotCoachQuestionRenderedEvent
  | JotCoachQuestionViewedEvent
  | JotCoachFeedbackUsefulEvent
  | JotCoachFeedbackNotUsefulEvent
  | JotCoachFeedbackCommentEvent;

/**
 * Documentation for each event type
 */
export const EVENT_TYPE_METADATA: Record<EventType, { description: string }> = {
  [EventType.ACTION_VIEWED]: {
    description: 'Triggered when a user views an action',
  },
  [EventType.ACTION_RENDERED]: {
    description: 'Triggered when an action is rendered',
  },
  [EventType.ACTION_INTERACTED]: {
    description: 'Triggered when a user interacts with an action',
  },
  [EventType.ACTION_REJECTED_REASON_PROVIDED]: {
    description: 'Triggered when a user rejects an action with specific reason',
  },
  [EventType.ACTION_EDITED]: {
    description: 'Triggered when a user edits an action',
  },
  [EventType.STRIPE_CONNECT_ONBOARDING_STARTED]: {
    description: 'Triggered when a user starts the Stripe connect onboarding',
  },
  [EventType.ENCOUNTER_SUBMITTED]: {
    description: 'Triggered when a user creates a note from the encounter view',
  },
  [EventType.FEEDBACK_SUBMITTED]: {
    description: 'Triggered when a user submits thumbs up/down feedback with optional metadata',
  },
  [EventType.EXPRESSED_INTEREST]: {
    description: 'Event Data for EXPRESSED_INTEREST event - tracks fake door test clicks',
  },
  [EventType.EXPRESSED_NOT_INTERESTED]: {
    description: 'Event Data for EXPRESSED_NOT_INTERESTED event - tracks when users dismiss or decline features',
  },
  [EventType.PDF_INTERACTED]: {
    description: 'Triggered when a PDF is downloaded, viewed, or printed',
  },
  [EventType.TOOLBAR_CLICKED]: {
    description: 'Triggered when a toolbar action is clicked',
  },
  [EventType.JOTCOACH_OVERVIEW_RENDERED]: {
    description: 'Triggered when JotCoach overview card enters viewport',
  },
  [EventType.JOTCOACH_OVERVIEW_VIEWED]: {
    description: 'Triggered when user hovers over JotCoach overview card',
  },
  [EventType.JOTCOACH_OVERVIEW_EXPANDED]: {
    description: 'Triggered when user expands JotCoach overview analysis',
  },
  [EventType.JOTCOACH_OVERVIEW_COLLAPSED]: {
    description: 'Triggered when user collapses JotCoach overview analysis',
  },
  [EventType.JOTCOACH_GAP_RENDERED]: {
    description: 'Triggered when a JotCoach gap card enters viewport',
  },
  [EventType.JOTCOACH_GAP_VIEWED]: {
    description: 'Triggered when user hovers over a JotCoach gap card',
  },
  [EventType.JOTCOACH_GAP_EXPANDED]: {
    description: 'Triggered when user expands a JotCoach gap suggested edit',
  },
  [EventType.JOTCOACH_GAP_COLLAPSED]: {
    description: 'Triggered when user collapses a JotCoach gap suggested edit',
  },
  [EventType.JOTCOACH_GAP_COPIED]: {
    description: 'Triggered when user copies JotCoach gap suggested text',
  },
  [EventType.JOTCOACH_QUESTION_RENDERED]: {
    description: 'Triggered when a JotCoach question card enters viewport',
  },
  [EventType.JOTCOACH_QUESTION_VIEWED]: {
    description: 'Triggered when user hovers over a JotCoach question card',
  },
  [EventType.JOTCOACH_FEEDBACK_USEFUL]: {
    description: 'Triggered when user marks JotCoach item as useful',
  },
  [EventType.JOTCOACH_FEEDBACK_NOT_USEFUL]: {
    description: 'Triggered when user marks JotCoach item as not useful',
  },
  [EventType.JOTCOACH_FEEDBACK_COMMENT]: {
    description: 'Triggered when user clicks to comment on JotCoach item',
  },
};
